<template>
    <tr :class="rowClass">
        <template v-if="!isLoading">
            <slot
                name="row"
                :rowData="row"
            >
                <td
                    v-if="hasVModel && allowMultiselect"
                    class="DataRow__check-box"
                >
                    <RPCheckbox
                        :value="isSelected"
                        @input="toggleSelect(row.id)"
                    />
                </td>
                <template v-for="header in headers">
                    <td
                        :key="header.key"
                        @click="rowSelect(row)"
                    >
                        <div class="DataRow__cell-contents">
                            <slot
                                :name="header.key"
                                :prop="header.key"
                                :rowData="row"
                            >
                                {{ row[header.key] }}
                            </slot>
                        </div>
                    </td>
                </template>
                <td
                    v-if="showActionCell"
                    class="DataRow__action-cell"
                >
                    <div class="DataRow__actions">
                        <slot
                            name="actionSlot"
                            :rowId="row.id"
                        ></slot>
                        <slot
                            name="extraSlot"
                            :rowId="row.id"
                            :rowData="row"
                        >
                            <RPHorizontalMenu
                                v-if="showExtraOptions"
                                class="DataRow__extra-options"
                            >
                                <slot
                                    name="extraOptions"
                                    :rowId="row.id"
                                    :rowData="row"
                                ></slot>
                            </RPHorizontalMenu>
                        </slot>
                        <slot name="modal"></slot>
                    </div>
                </td>
            </slot>
        </template>
        <template v-else>
            <td
                v-for="t in headerCount"
                :key="t"
            ></td>
        </template>
    </tr>
</template>
<script>
import RPCheckbox from 'components/Basic/FormElements/RPCheckbox.vue';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import RPHorizontalMenu from 'components/Basic/Buttons/RPHorizontalMenu';


export default {
    name: 'DataRow',
    components: {
        RPCheckbox,
        RPHorizontalMenu
    },
    props: {
        headers: {
            type: Array,
            required: true
        },
        row: {
            type: [Array, Object],
            required: true
        },
        tableType: {
            type: String,
            default: 'page',
            validator: val => ['page', 'card'].indexOf(val) !== -1,
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hasVModel: {
            type: Boolean,
            required: true,
        },
        allowMultiselect: {
            type: Boolean,
            default: false,
        },
        showActionCell: {
            type: Boolean,
            default: false,
        },
        showExtraOptions: {
            type: Boolean,
            default: false,
        },
        status: {
            type: String,
            default: stat.STANDBY,
        }
    },
    computed: {
        headerCount(){
            let c = this.headers.length;
            if(this.showActionCell) {
                c += 1;
            }
            if(this.hasVModel && this.allowMultiselect) {
                c += 1;
            }
            return c;
        },
        rowStatus(){
            return this.status === stat.LOADING
                ? this.status
                : this.row.status;
        },
        isLoading(){
            return this.rowStatus === stat.LOADING;
        },
        rowClass(){
            return statusBuilder(
                'DataRow',
                this.rowStatus,
                false,
                {
                    [this.tableType]: true,
                    'disabled': this.disabled,
                    'checked': this.isSelected && this.allowMultiselect,
                    'selected': this.isSelected && !this.allowMultiselect,
                }
            );
        },
    },
    methods: {
        toggleSelect(id){
            this.$emit('toggle-select', id);
        },
        rowSelect(row){
            this.$emit('row-select', row);
        },

    },
};
</script>
<style lang="scss">
    .DataRow {
        cursor: pointer;
        color: $grey;
        font-size: 1rem;
        height: 2rem;
        vertical-align: middle;
        td:first-child {
            border-left: .25rem solid transparent;
            padding-left: 0.5rem;
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
        }
        td:last-child {
            padding-right: 0.5rem;
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
        }
        td:nth-child(n+3):nth-last-child(n+2){
            overflow: hidden;
            text-overflow: ellipsis;
            padding: 0 0.5rem;
        }
        &__check-box {
            padding-right: 0.5rem;
            width: 0.1%;
            white-space: nowrap;
        }
        &__cell-contents{
            display: flex;
            align-items: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        &__action-cell {
            width: 0.1%;
            white-space: nowrap;
        }
        &__actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            margin: 0 0.5rem;
        }
        &__extra-options {
            margin-left: 0.5rem;
        }

        &--no-results{
            width: 100%;
            border-radius: 4px;
            white-space: nowrap;
        }

        @include loading(2rem, 100%);

        &--card {
            background: transparent;
            td:not(:first-child):not(:last-child) {
                border-top: 1px solid $grey-100;
                border-bottom: 1px solid $grey-100;
            }
            td:last-child {
                border-top: 1px solid $grey-100;
                border-bottom: 1px solid $grey-100;
                border-right: 1px solid $grey-100;
            }
            td:first-child {
                border-top: 1px solid $grey-100;
                border-bottom: 1px solid $grey-100;
                border-left: 1px solid $grey-100;
                padding-left: calc(.75rem - 1px);
            }
        }
        &--page {
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
            background: $white;
        }
        &--disabled td:first-child {
            border-left: .25rem solid $grey-100;
            padding-left: 0.5rem;
        }
        &--error td:first-child {
            border-left: .25rem solid $error-red;
            padding-left: 0.5rem;
        }
        &--warning td:first-child {
            border-left: .25rem solid $warning-yellow;
            padding-left: 0.5rem;
        }
        &--success td:first-child {
            border-left: .25rem solid $success-green;
            padding-left: 0.5rem;
        }
        &--selected {
            background: $primary-50;
            color: $primary;
        }
    }
</style>
